// src/components/Sidebar.tsx
import { useEffect } from 'react';
import type { Persona, Conversation, User } from '../types';
import styles from './Sidebar.module.css';

interface Props {
  persona: Persona;
  user: User;
  conversations: Conversation[];
  activeConvId: string | null;
  onNewChat: () => void;
  onLoadConv: (id: string) => void;
  onDeleteConv: (id: string) => void;
  onSwitchPersona: () => void;
  onLogout: () => void;
  onMounted: () => void;
}

function formatDate(raw: string): string {
  const dt    = new Date(raw);
  const now   = new Date();
  const delta = Math.floor((now.getTime() - dt.getTime()) / 86400000);
  if (delta === 0) return 'Today';
  if (delta === 1) return 'Yesterday';
  if (delta < 7)  return `${delta} days ago`;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function groupByDate(convos: Conversation[]): Record<string, Conversation[]> {
  const groups: Record<string, Conversation[]> = {};
  convos.forEach(c => {
    const label = formatDate(c.updated_at);
    if (!groups[label]) groups[label] = [];
    groups[label].push(c);
  });
  return groups;
}

export function Sidebar({
  persona, user, conversations, activeConvId,
  onNewChat, onLoadConv, onDeleteConv,
  onSwitchPersona, onLogout, onMounted,
}: Props) {

  useEffect(() => { onMounted(); }, [onMounted]);

  const groups = groupByDate(conversations);

  return (
    <aside className={styles.sidebar}>
      {/* Persona badge */}
      <div className={styles.top}>
        <div className={styles.badge}>
          <div className={styles.avatar}>{persona.avatar_letter}</div>
          <div>
            <div className={styles.personaName}>{persona.display_name}</div>
            <div className={styles.personaSub}>simulation active</div>
          </div>
        </div>
        <button className={styles.btnSwitch} onClick={onSwitchPersona}>
          ⇄ Switch Persona
        </button>
        <button className={styles.btnNew} onClick={onNewChat}>
          + New Chat
        </button>
      </div>

      {/* History */}
      <div className={styles.history}>
        <div className={styles.historyLabel}>// chat history</div>
        {conversations.length === 0 ? (
          <div className={styles.empty}>No past conversations yet.</div>
        ) : (
          Object.entries(groups).map(([label, convos]) => (
            <div key={label}>
              <div className={styles.groupLabel}>{label}</div>
              {convos.map(c => (
                <div
                  key={c._id}
                  className={`${styles.item} ${c._id === activeConvId ? styles.active : ''}`}
                >
                  <button
                    className={styles.itemBtn}
                    onClick={() => onLoadConv(c._id)}
                    title={c.title}
                  >
                    <span className={styles.itemTitle}>{c.title || 'Untitled'}</span>
                    <span className={styles.itemCount}>{c.message_count}</span>
                  </button>
                  <button
                    className={styles.itemDelete}
                    onClick={() => onDeleteConv(c._id)}
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Bottom user info + logout */}
      <div className={styles.bottom}>
        <div className={styles.userRow}>
          <div className={styles.userDot} />
          <span className={styles.userName}>{user.name}</span>
          <span className={styles.userRole}>{user.role}</span>
        </div>
        <button className={styles.btnLogout} onClick={onLogout}>
          logout
        </button>
      </div>
    </aside>
  );
}
